import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '../../lib/navigation';
import {
  BookOpen,
  ChevronRight,
  Users,
  Home,
  UserPlus,
  Navigation,
  LayoutDashboard,
  Shirt,
  UserCheck,
  ArrowLeftRight,
  Gavel,
  BarChart2,
  Award,
  Eye,
  Search,
  MessageSquare,
  ScrollText,
  HelpCircle,
  Settings,
  CreditCard,
  AlertTriangle,
  Zap,
  Info,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
} from 'lucide-react';
import { useThemeStore } from '../../lib/themeStore';
import { usePageTracking } from '../../lib/hooks/usePageTracking';

interface Section {
  id: string;
  title: string;
  icon: React.ElementType;
  subtitle?: string;
}

const SECTIONS: Section[] = [
  { id: 'intro', title: "Qu'est-ce que DynastyPool ?", icon: Home },
  { id: 'modes', title: 'Modes de ligue', icon: Zap, subtitle: 'Dynasty / Standard Flexible' },
  { id: 'compte', title: 'Créer un compte & connexion', icon: UserPlus },
  { id: 'navigation', title: 'Navigation générale', icon: Navigation },
  { id: 'dashboard', title: 'Tableau de bord', icon: LayoutDashboard },
  { id: 'myteam', title: 'Mon équipe', icon: Shirt },
  { id: 'freeagents', title: 'Agents libres', icon: UserCheck },
  { id: 'trades', title: 'Échanges (Trades)', icon: ArrowLeftRight },
  { id: 'auctions', title: 'Enchères actives', icon: Gavel },
  { id: 'tradingbloc', title: "Bloc d'échange", icon: BarChart2 },
  { id: 'standings', title: 'Classement', icon: Award },
  { id: 'draft', title: 'Repêchage (Draft)', icon: Award },
  { id: 'protection', title: 'Liste de protection', icon: ShieldCheck },
  { id: 'watchlist', title: 'Liste de surveillance', icon: Eye },
  { id: 'search', title: 'Recherche avancée', icon: Search },
  { id: 'messages', title: 'Centre de messages', icon: MessageSquare },
  { id: 'rules', title: 'Règlements de la ligue', icon: ScrollText },
  { id: 'faq', title: 'FAQ', icon: HelpCircle },
  { id: 'settings', title: 'Paramètres (commissaire)', icon: Settings },
  { id: 'payment', title: 'Paiement de la ligue', icon: CreditCard },
  { id: 'errors', title: 'Erreurs fréquentes', icon: AlertTriangle },
];

function TipBox({ children, variant = 'info' }: { children: React.ReactNode; variant?: 'info' | 'warning' | 'danger' | 'success' }) {
  const colors: Record<string, { bg: string; border: string; icon: string }> = {
    info:    { bg: 'rgba(59,130,246,0.08)',  border: '#3b82f6', icon: '💡' },
    warning: { bg: 'rgba(245,158,11,0.10)',  border: '#f59e0b', icon: '⚠️' },
    danger:  { bg: 'rgba(239,68,68,0.08)',   border: '#ef4444', icon: '❌' },
    success: { bg: 'rgba(34,197,94,0.08)',   border: '#22c55e', icon: '✅' },
  };
  const c = colors[variant];
  return (
    <div style={{ background: c.bg, borderLeft: `4px solid ${c.border}`, borderRadius: 6, padding: '10px 14px', marginTop: 10, marginBottom: 10, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>{c.icon}</span>
      <div style={{ fontSize: '0.9rem' }}>{children}</div>
    </div>
  );
}

function SectionCard({ id, title, icon: Icon, children, theme }: { id: string; title: string; icon: React.ElementType; children: React.ReactNode; theme: string }) {
  const isDark = theme === 'dark';
  return (
    <div
      id={id}
      className="mb-5"
      style={{
        background: isDark ? 'rgba(30,41,59,0.7)' : '#fff',
        border: isDark ? '1px solid rgba(59,130,246,0.15)' : '1px solid #e2e8f0',
        borderRadius: 12,
        padding: '28px 30px',
        scrollMarginTop: 130,
      }}
    >
      <div className="d-flex align-items-center gap-3 mb-4 pb-3" style={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0' }}>
        <div style={{ background: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(13,110,253,0.1)', borderRadius: 8, padding: 8 }}>
          <Icon size={22} color={isDark ? '#60a5fa' : '#0d6efd'} />
        </div>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b' }}>{title}</h2>
      </div>
      <div style={{ color: isDark ? '#cbd5e1' : '#374151', lineHeight: 1.7 }}>{children}</div>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: (string | React.ReactNode)[][] }) {
  return (
    <div className="table-responsive mt-2 mb-3">
      <table className="table table-bordered table-sm align-middle">
        <thead>
          <tr>
            {headers.map((h, i) => <th key={i} style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => <td key={ci}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StepList({ steps }: { steps: string[] }) {
  return (
    <ol className="ps-3 mt-2">
      {steps.map((s, i) => <li key={i} className="mb-1" dangerouslySetInnerHTML={{ __html: s }} />)}
    </ol>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="ps-3 mt-2">
      {items.map((s, i) => <li key={i} className="mb-1" dangerouslySetInnerHTML={{ __html: s }} />)}
    </ul>
  );
}

export function UserGuide() {
  usePageTracking('userguide');
  const navigate = useNavigate();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('intro');
  const [tocOpen, setTocOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Track active section via IntersectionObserver
  useEffect(() => {
    const ids = SECTIONS.map((s) => s.id);
    const map = new Map<string, number>();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          map.set(e.target.id, e.intersectionRatio);
        });
        // Pick the section with the highest visibility
        let best = '';
        let bestRatio = -1;
        map.forEach((ratio, id) => {
          if (ratio > bestRatio) { bestRatio = ratio; best = id; }
        });
        if (best) setActiveSection(best);
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: [0, 0.1, 0.5, 1] },
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observerRef.current?.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
    setTocOpen(false);
  };

  const tocBg = isDark ? 'rgba(15,23,42,0.95)' : '#f8fafc';
  const tocBorder = isDark ? '1px solid rgba(59,130,246,0.2)' : '1px solid #e2e8f0';
  const tocTopOffset = 92;

  return (
    <div className="card shadow-lg border-0" style={{ overflow: 'visible' }}>
      <div className="card-body p-0" style={{ overflow: 'visible' }}>
        {/* Header */}
        <div
          style={{
            background: isDark
              ? 'linear-gradient(135deg, rgba(30,41,59,0.95) 0%, rgba(15,23,42,0.95) 100%)'
              : 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
            padding: '28px 32px 24px',
            borderBottom: tocBorder,
          }}
        >
          <button
            className="btn btn-link p-0 mb-3 d-flex align-items-center gap-1"
            style={{ color: isDark ? '#60a5fa' : '#0d6efd', textDecoration: 'none', fontSize: '0.9rem' }}
            onClick={() => goBackOrHome(navigate)}
          >
            ← Retour
          </button>
          <div className="d-flex align-items-center gap-3">
            <div style={{ background: isDark ? 'rgba(59,130,246,0.2)' : 'rgba(13,110,253,0.12)', borderRadius: 12, padding: 12 }}>
              <BookOpen size={28} color={isDark ? '#60a5fa' : '#0d6efd'} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800, color: isDark ? '#f1f5f9' : '#1e293b' }}>
                🏒 Guide utilisateur DynastyPool
              </h1>
              <p style={{ margin: 0, marginTop: 4, color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.9rem' }}>
                Tout ce qu'il faut savoir pour gérer votre ligue fantasy hockey
              </p>
            </div>
          </div>
        </div>

        {/* ── Mobile TOC sticky bar (hors du flex, se colle en haut en scrollant) ── */}
        <div
          className="d-xl-none"
          style={{
            position: 'sticky',
            top: tocTopOffset,
            zIndex: 1005,
            borderBottom: tocBorder,
            background: tocBg,
            boxShadow: tocOpen
              ? (isDark ? '0 6px 24px rgba(0,0,0,0.45)' : '0 6px 24px rgba(0,0,0,0.14)')
              : (isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.07)'),
          }}
        >
          <button
            onClick={() => setTocOpen((o) => !o)}
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              padding: '11px 18px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              color: isDark ? '#f1f5f9' : '#1e293b',
              fontWeight: 600,
              fontSize: '0.9rem',
              gap: 8,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, overflow: 'hidden' }}>
              <Info size={15} style={{ flexShrink: 0, color: isDark ? '#60a5fa' : '#0d6efd' }} />
              <span style={{ flexShrink: 0 }}>Guide</span>
              {activeSection && (
                <span style={{
                  fontSize: '0.78rem',
                  fontWeight: 400,
                  color: isDark ? '#94a3b8' : '#64748b',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  — {SECTIONS.find((s) => s.id === activeSection)?.title}
                </span>
              )}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, fontSize: '0.75rem', color: isDark ? '#60a5fa' : '#0d6efd' }}>
              {SECTIONS.findIndex((s) => s.id === activeSection) + 1}/{SECTIONS.length}
              {tocOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          </button>
          {tocOpen && (
            <div style={{ background: tocBg, padding: '4px 10px 10px', maxHeight: 'calc(100dvh - 150px)', overflowY: 'auto' }}>
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const active = activeSection === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    style={{
                      display: 'flex', gap: 8, alignItems: 'center', width: '100%',
                      background: active ? (isDark ? 'rgba(59,130,246,0.15)' : 'rgba(13,110,253,0.08)') : 'transparent',
                      border: 'none',
                      borderLeft: active ? '3px solid #3b82f6' : '3px solid transparent',
                      borderRadius: '0 6px 6px 0',
                      padding: '8px 10px',
                      marginBottom: 2,
                      cursor: 'pointer',
                      color: active ? (isDark ? '#93c5fd' : '#0d6efd') : (isDark ? '#94a3b8' : '#475569'),
                      fontWeight: active ? 600 : 400,
                      fontSize: '0.86rem',
                      textAlign: 'left',
                      transition: 'all 0.12s',
                    }}
                  >
                    <Icon size={14} style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{s.title}</span>
                    {s.subtitle && <span style={{ fontSize: '0.72rem', opacity: 0.65 }}>— {s.subtitle}</span>}
                    {active && <span style={{ flexShrink: 0, color: isDark ? '#60a5fa' : '#0d6efd', fontSize: '0.65rem' }}>●</span>}
                  </button>
                );
              })}
              <button
                onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setTocOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                  background: 'transparent', border: 'none',
                  borderTop: tocBorder,
                  marginTop: 6, padding: '8px 10px',
                  cursor: 'pointer', color: isDark ? '#64748b' : '#94a3b8',
                  fontSize: '0.8rem', borderRadius: '0 0 6px 6px',
                }}
              >
                ↑ Revenir en haut
              </button>
            </div>
          )}
        </div>

        <div className="d-flex" style={{ minHeight: '80vh', alignItems: 'flex-start' }}>
          {/* ── Table des matières (desktop sticky) ── */}
          <aside
            className="d-none d-xl-block"
            style={{
              width: 260,
              minWidth: 260,
              background: tocBg,
              borderRight: tocBorder,
              position: 'sticky',
              top: tocTopOffset,
              alignSelf: 'flex-start',
              height: 'calc(100dvh - 92px)',
              maxHeight: 'calc(100vh - 92px)',
              zIndex: 100,
              overflowY: 'auto',
              flexShrink: 0,
            }}
          >
            <div className="p-3 pt-4">
              <p style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: isDark ? '#64748b' : '#94a3b8', marginBottom: 12 }}>
                Table des matières
              </p>
              <nav>
                {SECTIONS.map((s) => {
                  const Icon = s.icon;
                  const active = activeSection === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => scrollTo(s.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        width: '100%',
                        background: active ? (isDark ? 'rgba(59,130,246,0.15)' : 'rgba(13,110,253,0.08)') : 'transparent',
                        border: 'none',
                        borderLeft: active ? '3px solid #3b82f6' : '3px solid transparent',
                        borderRadius: '0 6px 6px 0',
                        padding: '7px 10px',
                        marginBottom: 2,
                        cursor: 'pointer',
                        color: active ? (isDark ? '#93c5fd' : '#0d6efd') : (isDark ? '#94a3b8' : '#475569'),
                        fontWeight: active ? 600 : 400,
                        fontSize: '0.82rem',
                        textAlign: 'left',
                        transition: 'all 0.15s',
                      }}
                    >
                      <Icon size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                      <div>
                        <div>{s.title}</div>
                        {s.subtitle && <div style={{ fontSize: '0.72rem', opacity: 0.7 }}>{s.subtitle}</div>}
                      </div>
                    </button>
                  );
                })}
              </nav>
              {/* Scroll to top */}
              <div style={{ borderTop: tocBorder, marginTop: 12, paddingTop: 10 }}>
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                    background: 'transparent', border: 'none', padding: '6px 10px',
                    cursor: 'pointer', color: isDark ? '#64748b' : '#94a3b8',
                    fontSize: '0.78rem', borderRadius: 6, transition: 'color 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = isDark ? '#93c5fd' : '#0d6efd')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = isDark ? '#64748b' : '#94a3b8')}
                >
                  ↑ Revenir en haut
                </button>
              </div>
            </div>
          </aside>

          {/* ── Contenu principal ── */}
          <main style={{ flex: 1, padding: '32px 28px', maxWidth: '100%', overflowX: 'hidden' }}>

            {/* 1 — Introduction */}
            <SectionCard id="intro" title="Qu'est-ce que DynastyPool ?" icon={Home} theme={theme}>
              <p>
                <strong>DynastyPool</strong> est une plateforme de gestion de ligues <em>fantasy hockey</em>. Chaque participant joue le rôle d'un{' '}
                <strong>directeur général (DG)</strong> qui gère une équipe de joueurs de la LNH. Votre objectif est d'accumuler le plus de{' '}
                <strong>points de fantaisie</strong> possible en vous basant sur les performances réelles de vos joueurs pendant la saison de hockey.
              </p>
              <h6 className="fw-bold mt-3">Ce que vous pouvez faire dans DynastyPool :</h6>
              <BulletList items={[
                'Gérer votre roster (liste de joueurs)',
                'Signer des agents libres',
                'Proposer et accepter des échanges avec d\'autres DG',
                'Participer au repêchage annuel',
                'Consulter le classement de la ligue',
                'Échanger avec les autres membres via le centre de messages',
              ]} />
            </SectionCard>

            {/* 2 — Modes de ligue */}
            <SectionCard id="modes" title="Modes de ligue : Dynasty / Keeper vs Standard Flexible" icon={Zap} theme={theme}>
              <p>DynastyPool propose <strong>deux types de ligues</strong>. Lors de la création ou de l'adhésion à une ligue, vous choisissez le mode qui convient le mieux à votre groupe.</p>

              <Table
                headers={['Fonctionnalité', 'Dynasty / Keeper', 'Standard Flexible']}
                rows={[
                  ['Durée', 'Multi-saisons (pool à vie)', 'Une saison seulement'],
                  ['Coût', '5,50 $ / usager', <span className="badge bg-success">Toujours gratuit</span>],
                  ['Joueurs exclusifs', '✅ Un joueur = une seule équipe', '❌ Partagés entre équipes'],
                  ['Repêchage (draft)', '✅ Serpentin ou linéaire', '❌ Pas de draft'],
                  ["Système d'échanges", '✅ Complet (avec retenue de salaire)', '❌ Pas d\'échanges'],
                  ['Enchères actives', '✅ Disponibles', '❌ Non disponibles'],
                  ["Bloc d'échange", '✅ Disponible', '❌ Non disponible'],
                  ['Liste de protection', '✅ Disponible en fin de saison', '❌ Non disponible'],                  
                  ['Phase de construction', '❌ Non applicable', '✅ Sélection libre en début de saison'],
                  ['Cap salarial / pointage', '✅ Configurable', '✅ Configurable'],
                ]}
              />

              <h5 className="fw-bold mt-4 d-flex align-items-center gap-2">
                <Zap size={18} color="#22c55e" /> Mode Standard Flexible — En détail
              </h5>
              <p>
                Le <strong>mode Standard Flexible</strong> est conçu pour des ligues légères, d'une seule saison, où plusieurs participants peuvent avoir les mêmes joueurs. Il est <span className="badge bg-success">entièrement gratuit</span> et ne nécessite pas de draft ni d'échanges.
              </p>

              <h6 className="fw-bold mt-3">Phase de construction initiale (Build Phase)</h6>
              <p>Lorsqu'un membre rejoint une ligue Standard Flexible pour la première fois, il entre en <strong>phase de construction</strong>.</p>
              <StepList steps={[
                'Rendez-vous dans la page <strong>"Bâtir mon équipe"</strong> (accessible depuis le tableau de bord ou la barre de navigation).',
                'La liste affiche <strong>tous les joueurs LNH actifs</strong>, sauf ceux déjà dans votre propre équipe. Les joueurs présents dans les équipes adverses restent disponibles pour vous.',
                'Cliquez sur <strong>"Signer"</strong> à côté du joueur souhaité.',
                'Choisissez la destination : <strong>Équipe principale</strong> (rapporte des points dès le début) ou <strong>Banc / Réserve</strong>.',
                'Continuez jusqu\'à atteindre les limites de position configurées par la ligue.',
                'Cliquez sur <strong>"Terminer la construction"</strong> pour confirmer votre alignement initial.',
              ]} />

              <TipBox variant="warning">
                Une fois la phase de construction terminée, cette page n'est plus accessible. Pour de nouvelles signatures, utilisez la page <strong>Agents libres</strong> régulière.
              </TipBox>
              <TipBox variant="info">
                Durant la phase de construction, les mêmes règles de plafond salarial et de limites de roster s'appliquent. Le compteur de signatures normales n'est <strong>pas</strong> utilisé pendant cette phase initiale.
              </TipBox>

              <h6 className="fw-bold mt-4">Fonctionnalités désactivées en Standard Flexible</h6>
              <Table
                headers={['Section désactivée', 'Raison']}
                rows={[
                  ['Repêchage (Draft)', 'La construction libre remplace la draft'],
                  ['Échanges (Trades)', "Les échanges n'existent pas dans ce mode"],
                  ['Enchères actives', 'Fonctionnalité exclusive au mode Dynasty'],
                  ["Bloc d'échange", 'Fonctionnalité exclusive au mode Dynasty'],
                  ['Liste de protection', 'Non applicable (une seule saison)'],                  
                ]}
              />

              <h6 className="fw-bold mt-4">Ce que le commissaire peut toujours configurer</h6>
              <BulletList items={[
                'Plafond salarial (Club principal et/ou Réserves)',
                'Limites de roster par position (attaquants, défenseurs, gardiens, réserves)',
                'Limite de signatures pour la saison régulière',
                'Système de pointage (buts, passes, victoires de gardiens, jeux blancs, etc.)',
                'Fenêtre sans mouvement (heures où les transactions sont bloquées)',
              ]} />

              <TipBox variant="info">
                Tant qu'un membre n'a pas terminé sa phase de construction, une <strong>bannière d'alerte</strong> apparaît dans le tableau de bord et sur la page Mon Équipe.
              </TipBox>
            </SectionCard>

            {/* 3 — Compte */}
            <SectionCard id="compte" title="Créer un compte & se connecter" icon={UserPlus} theme={theme}>
              <h6 className="fw-bold">Créer un compte</h6>
              <StepList steps={[
                'Ouvrez l\'application DynastyPool.',
                'Cliquez sur <strong>"S\'inscrire"</strong>.',
                'Entrez votre adresse courriel et un mot de passe.',
                'Confirmez votre inscription.',
              ]} />
              <TipBox variant="warning">
                Lors de votre première connexion, on vous demandera de fournir votre <strong>date de naissance</strong>. C'est obligatoire pour valider votre admissibilité.
              </TipBox>

              <h6 className="fw-bold mt-3">Se connecter</h6>
              <StepList steps={[
                'Sur la page d\'accueil, cliquez sur <strong>"Se connecter"</strong>.',
                'Entrez votre courriel et mot de passe.',
                'Vous serez redirigé vers le classement de votre ligue.',
              ]} />

              <h6 className="fw-bold mt-3">Rejoindre une ligue existante</h6>
              <StepList steps={[
                'Rendez-vous dans le <strong>Tableau de bord</strong>.',
                'Cliquez sur <strong>"Rejoindre une ligue"</strong>.',
                'Entrez le code d\'invitation fourni par le commissaire.',
                'Votre demande sera envoyée au commissaire pour approbation.',
              ]} />
              <TipBox variant="info">
                Le commissaire recevra une notification et devra <strong>approuver</strong> votre demande avant que vous puissiez participer.
              </TipBox>
            </SectionCard>

            {/* 4 — Navigation */}
            <SectionCard id="navigation" title="Navigation générale" icon={Navigation} theme={theme}>
              <p>La navigation principale se trouve dans la <strong>barre latérale gauche</strong> (ou en haut sur mobile). Voici les sections disponibles :</p>
              <Table
                headers={['Section', 'Description']}
                rows={[
                  ['🏠 Tableau de bord', 'Accueil et accès rapide aux fonctions'],
                  ['👕 Mon équipe', 'Gestion de votre roster'],
                  ['👥 Autres équipes', 'Consulter les rosters des adversaires'],
                  ['🆓 Agents libres', 'Joueurs disponibles à signer'],
                  ['👁️ Liste de surveillance', 'Joueurs que vous suivez'],
                  ['🔍 Recherche avancée', 'Trouver des joueurs selon des critères précis'],
                  ['🔄 Échanges', 'Proposer et gérer vos échanges'],
                  ['🏷️ Enchères actives', 'Gérer les offres d\'enchères en cours'],
                  ['📊 Bloc d\'échange', 'Voir les joueurs disponibles à l\'échange'],
                  ['📈 Classement', 'Voir le classement de la ligue'],
                  ['✉️ Messages', 'Offres d\'échange, demandes, violations'],
                  ['🎯 Repêchage', 'Participer au repêchage de la ligue'],
                  ['📋 Liste de protection', 'Protéger vos joueurs en fin de saison'],
                  ['📜 Historique', 'Historique des échanges et signatures'],
                  ['❓ FAQ', 'Questions et réponses fréquentes'],
                  ['⚙️ Paramètres', 'Réglages de la ligue (commissaire)'],
                ]}
              />
            </SectionCard>

            {/* 5 — Dashboard */}
            <SectionCard id="dashboard" title="Tableau de bord" icon={LayoutDashboard} theme={theme}>
              <p>Le <strong>tableau de bord</strong> est votre page principale. Il vous donne accès rapide aux fonctions les plus importantes.</p>
              <BulletList items={[
                '<strong>Créer une ligue</strong> — Pour lancer votre propre ligue (si vous êtes le commissaire)',
                '<strong>Sélectionner ma ligue</strong> — Si vous faites partie de plusieurs ligues, choisissez laquelle gérer',
                '<strong>Rejoindre une ligue</strong> — Entrer dans une ligue existante avec un code d\'invitation',
              ]} />
            </SectionCard>

            {/* 6 — Mon équipe */}
            <SectionCard id="myteam" title="Mon équipe" icon={Shirt} theme={theme}>
              <p>La page <strong>"Mon équipe"</strong> est l'endroit où vous gérez tous vos joueurs. C'est la section la plus importante pour votre performance en fantaisie.</p>

              <h6 className="fw-bold mt-3">Structure du roster</h6>
              <Table
                headers={['Section', 'Description']}
                rows={[
                  ['Club principal', 'Vos joueurs actifs qui accumulent des points chaque soir'],
                  ['Réserves', 'Joueurs sur votre liste d\'attente (comme dans les mineures)'],
                  ['Joueurs scratch', 'Joueurs inactifs, mis de côté temporairement'],
                ]}
              />
              <TipBox variant="info">
                <strong>Seuls les joueurs de votre Club Principal vous rapportent des points.</strong> Il est donc crucial de gérer vos montées et descentes intelligemment.
              </TipBox>

              <h6 className="fw-bold mt-4">Rétrograder un joueur</h6>
              <p className="text-muted small">Déplacer un joueur du Club principal vers les Réserves.</p>
              <StepList steps={[
                'Allez dans <strong>Mon équipe</strong>.',
                'Trouvez le joueur dans le Club principal.',
                'Cliquez sur le bouton <strong>"Rétrograder"</strong>.',
                'Confirmez votre action.',
              ]} />
              <TipBox variant="warning">
                Chaque rétrogradation utilise un crédit de votre <strong>quota de rétrogradations</strong> (limité pour toute la saison).
              </TipBox>

              <h6 className="fw-bold mt-4">Promouvoir un joueur</h6>
              <p className="text-muted small">Déplacer un joueur des Réserves ou du Scratch vers le Club principal.</p>
              <StepList steps={[
                'Allez dans <strong>Mon équipe</strong>.',
                'Trouvez le joueur dans les Réserves.',
                'Cliquez sur <strong>"Promouvoir"</strong>.',
                'Choisissez la destination : <strong>Club principal</strong> ou <strong>Scratch</strong>.',
              ]} />
              <TipBox variant="success">La promotion ne coûte <strong>aucun crédit</strong>.</TipBox>

              <h6 className="fw-bold mt-4">Permuter deux joueurs</h6>
              <p>La permutation échange la place d'un joueur du Club principal avec un joueur des Réserves en <strong>une seule action</strong>, ce qui coûte <strong>un seul crédit</strong>.</p>
              <StepList steps={[
                'Dans <strong>Mon équipe</strong>, cliquez sur <strong>"Permuter"</strong> à côté d\'un joueur du Club principal.',
                'Sélectionnez le joueur des Réserves qui prendra sa place.',
                'Confirmez.',
              ]} />
              <TipBox variant="success">C'est l'action <strong>recommandée</strong> pour gérer votre roster efficacement !</TipBox>

              <h6 className="fw-bold mt-4">Permutation 4 joueurs</h6>
              <p>Action avancée : faire monter <strong>2 joueurs des Réserves</strong> et descendre <strong>2 joueurs du Club principal</strong> en une seule manipulation.</p>
              <StepList steps={[
                'Activez le <strong>mode multi-sélection</strong> dans Mon équipe.',
                'Sélectionnez <strong>2 joueurs du Club principal</strong> à descendre.',
                'Sélectionnez <strong>2 joueurs des Réserves</strong> à monter.',
                'Confirmez la permutation.',
              ]} />
              <TipBox variant="warning">Cette action coûte <strong>2 crédits de rétrogradation</strong>.</TipBox>

              <h6 className="fw-bold mt-4">Réactiver un joueur blessé</h6>
              <StepList steps={[
                'Trouvez le joueur dans la section <strong>Blessés</strong>.',
                'Cliquez sur <strong>"Réactiver"</strong>.',
                'Choisissez : <strong>Retour direct</strong> (retour dans sa section d\'origine) ou <strong>Permuter</strong> (échange avec un joueur actif — aucun crédit supplémentaire).',
              ]} />

              <h6 className="fw-bold mt-4">Libérer un joueur</h6>
              <TipBox variant="danger">
                Libérer un joueur le remet dans le bassin des agents libres. Cette action est <strong>irréversible</strong>. Un autre DG pourrait le signer immédiatement.
              </TipBox>

              <h6 className="fw-bold mt-4">Plafond salarial</h6>
              <p>Si votre ligue utilise un plafond salarial, vous verrez en tout temps : <code>Salaire utilisé / Plafond maximum</code></p>              
            </SectionCard>

            {/* 7 — Agents libres */}
            <SectionCard id="freeagents" title="Agents libres" icon={UserCheck} theme={theme}>
              <p>La page <strong>"Agents libres"</strong> affiche tous les joueurs disponibles à signer dans votre ligue.</p>

              <h6 className="fw-bold mt-3">Comment signer un agent libre</h6>
              <StepList steps={[
                'Allez dans <strong>Agents libres</strong>.',
                'Utilisez les filtres : Position, Équipe LNH, Fourchette de salaire, Nom du joueur.',
                'Cliquez sur <strong>"Signer"</strong> à côté du joueur.',
                'Choisissez sa destination : <strong>Club principal</strong>, <strong>Réserves</strong> ou <strong>Scratch</strong>.',
                'Confirmez la signature.',
              ]} />

              <h6 className="fw-bold mt-3">Règles importantes</h6>
              <BulletList items={[
                'Quota de signatures limité par saison (compteur affiché sur la page)',
                'Pas de dépassement des limites par position',
                'Le salaire du joueur doit respecter votre plafond salarial disponible',
              ]} />
              <TipBox variant="danger">Si une limite est atteinte, l'application vous bloquera et vous indiquera la raison exacte.</TipBox>
            </SectionCard>

            {/* 8 — Échanges */}
            <SectionCard id="trades" title="Échanges (Trades)" icon={ArrowLeftRight} theme={theme}>
              <TipBox variant="warning">Les échanges sont disponibles uniquement en mode <strong>Dynasty / Keeper</strong>.</TipBox>

              <h6 className="fw-bold mt-3">Proposer un échange</h6>
              <StepList steps={[
                'Allez dans <strong>Échanges</strong>.',
                'Sélectionnez le <strong>DG adverse</strong> dans la liste déroulante.',
                'Choisissez les joueurs / choix de repêchage que <strong>vous offrez</strong>.',
                'Choisissez les joueurs / choix de repêchage que <strong>vous souhaitez recevoir</strong>.',
                '(Facultatif) Ajoutez une <strong>retenue de salaire</strong> si disponible.',
                'Cliquez sur <strong>"Proposer l\'échange"</strong>.',
              ]} />

              <h6 className="fw-bold mt-3">Répondre à une offre reçue</h6>
              <StepList steps={[
                'Allez dans <strong>Messages → Offres d\'échange</strong>.',
                'Consultez les détails de l\'offre.',
                'Choisissez : ✅ <strong>Accepter</strong> / ❌ <strong>Refuser</strong> / 🔄 <strong>Contre-offre</strong>.',
              ]} />

              <h6 className="fw-bold mt-3">Retenue de salaire</h6>
              <p>Si votre ligue le permet, vous pouvez retenir une partie du salaire d'un joueur que vous échangez. Cela réduit l'impact salarial pour l'équipe qui le reçoit.</p>
              <BulletList items={[
                'Vous choisissez le <strong>pourcentage</strong> à retenir',
                'Limite maximale de retenues actives par équipe (généralement 3)',
                'Le montant retenu reste une dette dans vos finances d\'équipe',
              ]} />

              <h6 className="fw-bold mt-3">Option validation automatique</h6>
              <p>Si activée, avant d'être complété, l'application vérifie automatiquement que les deux équipes respectent les limites de joueurs par position, le plafond salarial, et les limites des Réserves.</p>
            </SectionCard>

            {/* 9 — Enchères */}
            <SectionCard id="auctions" title="Enchères actives (En développement)" icon={Gavel} theme={theme}>
              <TipBox variant="warning">Les enchères sont disponibles uniquement en mode <strong>Dynasty / Keeper</strong>.</TipBox>
              <p className="mt-2">Le système d'enchères permet à plusieurs DG de soumissionner pour des échanges de façon compétitive.</p>
              <StepList steps={[
                'Un échange est mis aux enchères par un DG.',
                'Les autres DG peuvent <strong>soumettre des offres</strong>.',
                'Des <strong>contre-offres</strong> peuvent être proposées.',
                "L'enchère se termine lorsque l'offre est acceptée ou que le délai expire.",
              ]} />
            </SectionCard>

            {/* 10 — Bloc d'échange */}
            <SectionCard id="tradingbloc" title="Bloc d'échange (Trading Bloc)" icon={BarChart2} theme={theme}>
              <TipBox variant="warning">Disponible uniquement en mode <strong>Dynasty / Keeper</strong>.</TipBox>
              <p className="mt-2">Le <strong>Bloc d'échange</strong> est une vitrine publique où les DG affichent les joueurs qu'ils sont prêts à échanger.</p>

              <Table
                headers={['Couleur du contour', 'Signification']}
                rows={[
                  ['🟡 Jaune', 'Joueur disponible à l\'échange'],
                  ['🔴 Rouge', 'Joueur intouchable (non disponible)'],
                ]}
              />

              <h6 className="fw-bold mt-3">Proposer un échange depuis le Bloc</h6>
              <StepList steps={[
                'Repérez un joueur qui vous intéresse.',
                'Cliquez sur <strong>"Proposer un échange"</strong>.',
                'Vous serez redirigé vers la page d\'échange avec ce joueur pré-sélectionné.',
              ]} />
            </SectionCard>

            {/* 11 — Classement */}
            <SectionCard id="standings" title="Classement (Standings)" icon={Award} theme={theme}>
              <Table
                headers={['Onglet', 'Ce qu\'il montre']}
                rows={[
                  ['Total', 'Points cumulés depuis le début de la saison'],
                  ['De la veille', 'Points marqués uniquement hier'],
                  ['Stats Live', 'Statistiques en temps réel (mise à jour automatique)'],
                ]}
              />

              <h6 className="fw-bold mt-4">Comment fonctionne le pointage ?</h6>
              <p>Les joueurs de votre <strong>Club principal</strong> vous rapportent des points selon leurs performances en LNH.</p>

              <div className="row g-3 mt-1">
                {[
                  { pos: 'Attaquants', rows: [['But', '1 point'], ['Passe', '1 point']] },
                  { pos: 'Défenseurs', rows: [['But', '2 points'], ['Passe', '1 point']] },
                  { pos: 'Gardiens', rows: [['Victoire', '2 points'], ['Jeu blanc', '3 points'], ['But encaissé en prolongation', '1 point']] },
                ].map(({ pos, rows }) => (
                  <div key={pos} className="col-md-4">
                    <div style={{ background: isDark ? 'rgba(59,130,246,0.08)' : '#f1f5f9', borderRadius: 8, padding: '14px 16px' }}>
                      <p className="fw-bold mb-2 small text-uppercase" style={{ letterSpacing: '0.05em', color: isDark ? '#60a5fa' : '#0d6efd' }}>{pos}</p>
                      {rows.map(([perf, pts]) => (
                        <div key={perf} className="d-flex justify-content-between small">
                          <span>{perf}</span><span className="fw-bold">{pts}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <TipBox variant="warning">Les règles de pointage peuvent varier selon votre ligue. Consultez la section <strong>Règlements</strong> pour connaître les valeurs exactes.</TipBox>
            </SectionCard>

            {/* 12 — Repêchage */}
            <SectionCard id="draft" title="Repêchage (Draft)" icon={Award} theme={theme}>
              <TipBox variant="warning">Disponible uniquement en mode <strong>Dynasty / Keeper</strong>.</TipBox>

              <h6 className="fw-bold mt-3">Avant le repêchage</h6>
              <BulletList items={[
                'Préparez votre <strong>liste de priorité</strong> : ordonnez les joueurs que vous souhaitez drafter.',
                'Si le minuteur expire lors de votre tour, le système sélectionnera automatiquement le premier joueur disponible sur votre liste.',
              ]} />

              <h6 className="fw-bold mt-3">Pendant le repêchage</h6>
              <StepList steps={[
                'Naviguez dans la liste des joueurs disponibles.',
                'Cliquez sur le joueur que vous souhaitez.',
                'Choisissez sa destination : <strong>Club principal</strong> ou <strong>Réserves</strong>.',
                'Confirmez votre sélection.',
              ]} />

              <h6 className="fw-bold mt-3">Liste de protection (fin de saison)</h6>
              <p>En fin de saison, certaines ligues exigent que les DG soumettent une <strong>liste de protection</strong>. Les joueurs non protégés peuvent être récupérés lors du prochain repêchage d'expansion. Consultez la section dédiée ci-dessous pour tous les détails.</p>
            </SectionCard>

            {/* 13 — Liste de protection */}
            <SectionCard id="protection" title="Liste de protection" icon={ShieldCheck} theme={theme}>
              <TipBox variant="warning">Disponible uniquement en mode <strong>Dynasty / Keeper</strong>. Cette fonctionnalité est activée par le commissaire en fin de saison.</TipBox>

              <p className="mt-2">
                La <strong>liste de protection</strong> permet à chaque DG de désigner les joueurs qu'il souhaite conserver dans son équipe d'une saison à l'autre. Les joueurs <strong>non protégés</strong> sont remis dans le bassin et peuvent être récupérés lors du prochain repêchage d'expansion ou de la prochaine draft.
              </p>

              <h6 className="fw-bold mt-4">Quand soumettre votre liste ?</h6>
              <p>Le commissaire fixe une <strong>date limite</strong> pour la soumission des listes de protection. Passé ce délai, les joueurs non protégés sont automatiquement libérés. Consultez la page <strong>Règlements</strong> ou les <strong>Messages de la ligue</strong> pour connaître la date exacte.</p>

              <h6 className="fw-bold mt-4">Comment soumettre votre liste de protection</h6>
              <StepList steps={[
                'Allez dans la section <strong>"Liste de protection"</strong> depuis la barre de navigation.',
                'La page affiche tous vos joueurs actuels (Club principal + Réserves).',
                'Cochez les joueurs que vous souhaitez <strong>protéger</strong> en cliquant sur leur carte ou la case correspondante.',
                'Respectez le <strong>nombre maximum de joueurs protégeables</strong> affiché en haut de la page (défini par le commissaire).',
                'Cliquez sur <strong>"Soumettre ma liste"</strong> pour confirmer votre sélection.',
                'Un récapitulatif de vos choix s\'affiche. Confirmez pour finaliser.  ',
              ]} />

              <TipBox variant="info">
                Vous pouvez <strong>modifier</strong> votre liste de protection autant de fois que vous le souhaitez <em>avant</em> la date limite. Seule la dernière soumission est prise en compte.
              </TipBox>

              <h6 className="fw-bold mt-4">Règles et limites</h6>
              <Table
                headers={['Règle', 'Détail']}
                rows={[
                  ['Nombre de joueurs protégeables', 'Défini par le commissaire (ex. : 10, 15, 20 joueurs)'],
                  ['Joueurs non protégés', 'Remis dans le bassin des agents libres ou repêchables'],
                  ['Joueurs blessés (LTI / Réserve)', 'Peuvent être protégés comme les autres joueurs'],
                  ['Soumission tardive', "Si aucune liste n'est soumise avant la date limite, aucun joueur n'est protégé automatiquement"],
                ]}
              />

              <TipBox variant="danger">
                Si vous ne soumettez <strong>aucune liste</strong> avant la date limite, <strong>tous vos joueurs</strong> pourraient être libérés selon les règles de votre ligue. Ne manquez pas cette étape cruciale !
              </TipBox>

              <h6 className="fw-bold mt-4">Après la soumission — que se passe-t-il ?</h6>
              <BulletList items={[
                'Le commissaire finalise la saison et lance le processus de libération des joueurs non protégés.',
                'Les joueurs non protégés apparaissent dans le bassin du prochain repêchage ou dans les agents libres.',
                'Votre équipe pour la prochaine saison sera composée <strong>uniquement</strong> de vos joueurs protégés.',
                'La draft de la nouvelle saison permettra ensuite de compléter votre roster.',
              ]} />

              <TipBox variant="success">
                Conseil stratégique : priorisez vos <strong>jeunes joueurs à fort potentiel</strong> et vos <strong>piliers offensifs</strong>. Un joueur non protégé peut être réclamé par un adversaire !
              </TipBox>
            </SectionCard>

            {/* 14 — Watchlist */}
            <SectionCard id="watchlist" title="Liste de surveillance (Watchlist)" icon={Eye} theme={theme}>
              <p>La <strong>Liste de surveillance</strong> vous permet de suivre des joueurs qui vous intéressent, qu'ils soient agents libres ou déjà sous contrat.</p>

              <h6 className="fw-bold mt-3">Ajouter un joueur</h6>
              <BulletList items={[
                'Depuis <strong>Agents libres</strong> : cliquez sur l\'icône 👁️ à côté du joueur.',
                'Depuis <strong>Mon équipe</strong> ou <strong>Autres équipes</strong> : même procédure.',
              ]} />

              <h6 className="fw-bold mt-3">Informations affichées</h6>
              <BulletList items={[
                'Photo, nom, position, équipe LNH',
                'Salaire du joueur',
                'Statut (Agent libre ou nom du propriétaire)',
                'Date d\'ajout à votre liste',
              ]} />
              <TipBox variant="info">
                Votre liste de surveillance peut aussi servir de <strong>liste de priorité</strong> lors du repêchage. Si le minuteur expire, le système peut choisir automatiquement le premier joueur disponible de votre liste.
              </TipBox>
            </SectionCard>

            {/* 15 — Recherche avancée */}
            <SectionCard id="search" title="Recherche avancée de joueurs" icon={Search} theme={theme}>
              <Table
                headers={['Filtre', 'Description']}
                rows={[
                  ['Nom', 'Recherche par nom (minimum 3 caractères)'],
                  ['Équipe LNH', 'Filtrer par équipe (ex. : MTL, TOR, BOS)'],
                  ['Position', 'C, AG, AD, D, G'],
                  ['Statut', 'Agent libre, LNH, Réserve, Blessé, Scratch'],
                  ['Salaire', 'Fourchette minimum/maximum'],
                  ['Points', 'Minimum/maximum de points'],
                  ['Buts', 'Minimum/maximum de buts'],
                  ['Passes', 'Minimum/maximum de passes'],
                ]}
              />
              <TipBox variant="info">La recherche affiche un maximum de <strong>100 résultats</strong>. Affinez vos critères si vous ne trouvez pas ce que vous cherchez.</TipBox>
            </SectionCard>

            {/* 16 — Centre de messages */}
            <SectionCard id="messages" title="Centre de messages" icon={MessageSquare} theme={theme}>
              <Table
                headers={['Onglet', 'Contenu']}
                rows={[
                  ['Offres d\'échange', 'Toutes les propositions d\'échange reçues et envoyées'],
                  ['Messages de la ligue', 'Annonces générales'],
                  ['Demandes d\'adhésion', '(Commissaire) Demandes de nouveaux membres'],
                  ['Violations de roster', 'Alertes si votre roster dépasse les règles'],
                ]}
              />
              <TipBox variant="warning">
                Si votre équipe ne respecte plus les règles (ex. : trop de joueurs dans une position), vous recevrez une <strong>alerte de violation</strong>. Vous devez corriger le problème avant la prochaine date limite.
              </TipBox>
            </SectionCard>

            {/* 17 — Règlements */}
            <SectionCard id="rules" title="Règlements de la ligue" icon={ScrollText} theme={theme}>
              <p>La page <strong>Règlements</strong> affiche les règles spécifiques à votre ligue, définies par le commissaire.</p>
              <BulletList items={[
                'Le système de pointage exact',
                'Les conditions particulières pour les transactions',
                'Les règles de repêchage',
                'Toute règle spéciale décidée par les membres',
              ]} />
              <TipBox variant="info">Consultez cette page régulièrement, surtout en début de saison.</TipBox>
            </SectionCard>

            {/* 18 — FAQ */}
            <SectionCard id="faq" title="FAQ — Questions fréquentes" icon={HelpCircle} theme={theme}>
              <p>La section <strong>FAQ</strong> contient des réponses aux questions les plus courantes sur l'utilisation de l'application.</p>

              <Table
                headers={['Statut', 'Signification']}
                rows={[
                  ['🟢 Ouverte', 'Question posée, en attente de réponse'],
                  ['✅ Répondue', 'Une réponse officielle a été fournie'],
                  ['🔒 Fermée', 'Question archivée'],
                ]}
              />

              <p className="mt-2">Catégories disponibles : Général, Règlements, Échanges, Repêchage, Salaires, Technique.</p>
              <TipBox variant="info">Si vous ne trouvez pas de réponse, vous pouvez soumettre votre propre question depuis la FAQ.</TipBox>
            </SectionCard>

            {/* 19 — Paramètres */}
            <SectionCard id="settings" title="Paramètres de la ligue (commissaire uniquement)" icon={Settings} theme={theme}>
              <TipBox variant="warning">Cette section est <strong>réservée au commissaire</strong> de la ligue.</TipBox>

              <Table
                headers={['Paramètre', 'Description']}
                rows={[
                  ['Plafond salarial', 'Montant maximum autorisé pour le Club principal'],
                  ['Plafond des réserves', 'Montant maximum autorisé pour les Réserves'],
                  ['Limite de rétrogradations', 'Nombre maximum de rétrogradations par saison'],
                  ['Limite de signatures', 'Nombre maximum de signatures par saison'],
                  ['Spots de retenue', 'Nombre maximum de retenues salariales actives par équipe'],
                  ['Date limite d\'échange', 'Après cette date, aucun échange n\'est possible'],
                  ['Date limite de mouvements', 'Après cette date, les promotions et rétrogradations (mouvements entre Club principal, Réserves et Scratch) ne sont plus autorisées'],
                  ['Échange de choix de repêchage', 'Permettre ou interdire les échanges de choix de repêchage et nombre de rondes échangeables'],
                  ['Mode repêchage', 'Activer/désactiver le repêchage en cours'],
                  ['Fenêtre sans mouvement', 'Heures durant lesquelles les transactions sont bloquées'],
                  ['Règles spécifiques', 'Texte libre pour décrire les règles maison'],
                ]}
              />
            </SectionCard>

            {/* 20 — Paiement */}
            <SectionCard id="payment" title="Paiement de la ligue (propulsé par Stripe)" icon={CreditCard} theme={theme}>
              <TipBox variant="info">Les ligues <strong>Standard Flexible</strong> sont toujours gratuites. Le paiement s'applique uniquement au mode Dynasty / Keeper.</TipBox>
              <StepList steps={[
                'Allez sur la page <strong>Paiement de la ligue</strong>.',
                'Vérifiez le montant dû.',
                'Entrez vos informations de paiement.',
                'Confirmez. Vous recevrez une confirmation à l\'écran.',
              ]} />
              <TipBox variant="success">Le paiement est sécurisé. Vos informations bancaires ne sont jamais stockées dans l'application.</TipBox>
            </SectionCard>

            {/* 21 — Erreurs fréquentes */}
            <SectionCard id="errors" title="Erreurs fréquentes et comment les éviter" icon={AlertTriangle} theme={theme}>
              {[
                {
                  title: '"Limite de rétrogradations atteinte"',
                  cause: 'Vous avez utilisé tous vos crédits de rétrogradation pour la saison.',
                  solution: 'Vous devez attendre que le commissaire augmente ou réinitialise les mouvements',
                },
                {
                  title: '"Plafond salarial dépassé"',
                  cause: 'La transaction dépasse votre plafond salarial autorisé.',
                  solution: 'Libérez un joueur à haut salaire avant de signer, proposez un échange qui réduit votre masse salariale, ou utilisez la retenue de salaire si disponible.',
                },
                {
                  title: '"Limite de position atteinte"',
                  cause: 'Vous essayez d\'ajouter un joueur dans une position déjà pleine (ex. : trop d\'attaquants).',
                  solution: 'Libérez ou échangez un joueur de la même position avant d\'en ajouter un nouveau.',
                },
                {
                  title: '"Demande d\'adhésion en attente"',
                  cause: 'Vous avez rejoint une ligue mais le commissaire n\'a pas encore approuvé votre demande.',
                  solution: 'Patientez. Le commissaire recevra une notification et devra approuver manuellement votre demande.',
                },
                {
                  title: '"Aucun mouvement autorisé en ce moment"',
                  cause: 'Votre ligue a une <strong>fenêtre sans mouvement</strong> active (généralement pendant les soirs de match).',
                  solution: 'Attendez que la fenêtre soit terminée et effectuez vos changements avant la prochaine fenêtre.',
                },
                {
                  title: '"Date limite de mouvements dépassée"',
                  cause: 'Le commissaire a fixé une date après laquelle les promotions et rétrogradations ne sont plus permises.',
                  solution: 'Vous ne pouvez plus déplacer de joueurs entre le Club principal, les Réserves et le Scratch. Contactez le commissaire si vous pensez que c\'est une erreur.',
                },
                {
                  title: "L'échange est refusé automatiquement",
                  cause: "L'échange proposé ferait dépasser une limite chez l'équipe adverse.",
                  solution: 'Relisez le message d\'erreur. Ajustez les joueurs inclus ou négociez une retenue de salaire.',
                },
                {
                  title: 'Le joueur blessé ne peut pas être réactivé directement',
                  cause: 'Votre roster est plein à la position concernée.',
                  solution: 'Utilisez l\'option <strong>"Réactiver en permutant"</strong> : choisissez un joueur actif à sa place, les deux seront échangés sans coût supplémentaire.',
                },
                {
                  title: 'Mon nom d\'utilisateur apparaît mal',
                  cause: '',
                  solution: 'Allez dans <strong>"Modifier mon nom d\'utilisateur"</strong> (accessible depuis votre profil ou les paramètres). Choisissez un nouveau nom et confirmez.',
                },
              ].map(({ title, cause, solution }) => (
                <div key={title} className="mb-4 pb-4" style={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0' }}>
                  <p className="fw-bold mb-1 d-flex align-items-center gap-2">
                    <AlertTriangle size={15} color="#ef4444" />
                    {title}
                  </p>
                  {cause && <p className="text-muted small mb-1" dangerouslySetInnerHTML={{ __html: `<strong>Cause :</strong> ${cause}` }} />}
                  <p className="small mb-0" dangerouslySetInnerHTML={{ __html: `<strong>Solution :</strong> ${solution}` }} />
                </div>
              ))}
            </SectionCard>

            {/* Footer */}
            <div className="text-center py-4" style={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: '0.82rem' }}>
              <ChevronRight size={14} className="me-1" />
              Pour toute question non couverte, consultez la <button className="btn btn-link p-0 small" style={{ color: isDark ? '#60a5fa' : '#0d6efd', verticalAlign: 'baseline' }} onClick={() => navigate('/faq')}>FAQ de votre ligue</button> ou contactez le commissaire.
            </div>

          </main>
        </div>
      </div>
    </div>
  );
}
